import { memo } from 'react'

import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  filterLabelsByRegex,
  sortPDFLabelSetsAlphabetically,
  procurifyDefaultPDFLabelSet,
} from './utils'
import { pdfLabelSetRepository } from '../../data-access/repositories/PDFLabelSetRepository'
import { PDFLabelSet } from '../../entities/PDFLabelSet'

export interface IPDFLabelSetSelectProps extends IAsyncSelectProps<PDFLabelSet> {}

export const loadOptions = async (
  inputValue: string,
  pagination: any,
  callback: Function
) => {
  try {
    const res = await pdfLabelSetRepository.list({
      search: inputValue,
      page: pagination.page,
    })

    const labelSets = res.data.map((labelSet) => new PDFLabelSet(labelSet))
    const allLabelSets = [...labelSets, procurifyDefaultPDFLabelSet]

    const sortedLabelSets = sortPDFLabelSetsAlphabetically(allLabelSets)

    const filteredLabelSets = inputValue
      ? filterLabelsByRegex(inputValue, sortedLabelSets)
      : sortedLabelSets

    callback({
      options: filteredLabelSets,
      pagination: {
        hasMore: res.pagination
          ? res.pagination.page < res.pagination.numPages
          : false,
        page: pagination.page + 1,
      },
    })
  } catch (error) {
    callback({
      options: [],
      pagination: {
        hasMore: false,
        page: 1,
      },
    })
  }
}

export const PDFLabelSetSelect = memo((props: IPDFLabelSetSelectProps) => {
  const intl = useProcurifyIntl()

  return (
    <AsyncSelect<PDFLabelSet>
      cacheOptions={true}
      defaultOptions={true}
      getOptionLabel={(option) => {
        return option.isPrimary
          ? `${option.value.name} ${intl.formatMessage(messages.PDFLabelSetDefaultLabel)}`
          : option.value.name
      }}
      getOptionValue={(option) => option.value.name}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.PDFLabelSetSelectPlaceholder)}
      {...props}
    />
  )
})

PDFLabelSetSelect.displayName = 'PDFLabelSetSelect'
